import type { Tile } from '../types/dualGame';

let globalTileId = 0;
export const nextTileId = () => ++globalTileId;
export const resetTileIds = () => { globalTileId = 0; };

export const createEmptyBoard = (size: number): number[][] =>
  Array.from({ length: size }, () => Array(size).fill(0));

export const addRandomTile = (board: number[][]): { board: number[][]; tile: Tile | null } => {
  const empty: [number, number][] = [];
  board.forEach((row, i) => row.forEach((v, j) => { if (v === 0) empty.push([i, j]); }));
  if (empty.length === 0) return { board, tile: null };

  const [r, c] = empty[Math.floor(Math.random() * empty.length)];
  const value = Math.random() < 0.9 ? 2 : 4;
  const newBoard = board.map(row => [...row]);
  newBoard[r][c] = value;
  return {
    board: newBoard,
    tile: { id: nextTileId(), value, row: r, col: c, isNew: true },
  };
};

export type Direction = 'up' | 'down' | 'left' | 'right';

export interface MoveResult {
  newBoard: number[][];
  newTiles: Tile[];
  scoreDelta: number;
  hasMoved: boolean;
  hasMerge: boolean;
}

export const moveBoard = (board: number[][], existingTiles: Tile[], direction: Direction): MoveResult => {
  const size = board.length;
  let scoreDelta = 0;
  let hasMoved = false;
  let hasMerge = false;

  const tileAt = new Map<string, Tile>();
  existingTiles.forEach(t => tileAt.set(`${t.row},${t.col}`, t));

  const resultTiles: Tile[] = [];
  const newBoard = createEmptyBoard(size);

  for (let lineIdx = 0; lineIdx < size; lineIdx++) {
    const positions: [number, number][] = [];
    for (let i = 0; i < size; i++) {
      switch (direction) {
        case 'left':  positions.push([lineIdx, i]); break;
        case 'right': positions.push([lineIdx, size - 1 - i]); break;
        case 'up':    positions.push([i, lineIdx]); break;
        case 'down':  positions.push([size - 1 - i, lineIdx]); break;
      }
    }

    const lineTiles: Tile[] = [];
    for (const [r, c] of positions) {
      const t = tileAt.get(`${r},${c}`);
      if (t) lineTiles.push(t);
    }

    let outIdx = 0;
    let i = 0;
    while (i < lineTiles.length) {
      const [r, c] = positions[outIdx];
      if (i + 1 < lineTiles.length && lineTiles[i].value === lineTiles[i + 1].value) {
        const mergedValue = lineTiles[i].value * 2;
        scoreDelta += mergedValue;
        hasMerge = true;
        resultTiles.push({ id: lineTiles[i].id, value: mergedValue, row: r, col: c, isMerged: true });
        newBoard[r][c] = mergedValue;
        i += 2;
      } else {
        const old = lineTiles[i];
        if (old.row !== r || old.col !== c) hasMoved = true;
        resultTiles.push({ ...old, row: r, col: c, isNew: false, isMerged: false });
        newBoard[r][c] = old.value;
        i++;
      }
      outIdx++;
    }
  }

  hasMoved = hasMoved || hasMerge;
  return { newBoard, newTiles: resultTiles, scoreDelta, hasMoved, hasMerge };
};

export const canMove = (board: number[][]): boolean => {
  const size = board.length;
  for (let i = 0; i < size; i++) {
    for (let j = 0; j < size; j++) {
      if (board[i][j] === 0) return true;
      if (j < size - 1 && board[i][j + 1] === board[i][j]) return true;
      if (i < size - 1 && board[i + 1][j] === board[i][j]) return true;
    }
  }
  return false;
};

export const hasReachedValue = (tiles: Tile[], target: number): boolean =>
  tiles.some(t => t.value >= target);
